import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../apiClientContext";
import { useKeepAdjacentData } from "../queryPlaceholders";
import {
    addManagementGroupAdmin,
    addTeamMember,
    createManagementGroup,
    createTeam,
    deleteManagementGroup,
    deleteTeam,
    fetchManagementGroup,
    fetchManagementGroups,
    pushTeamToQueue,
    removeManagementGroupAdmin,
    removeTeamMember,
    renameManagementGroup,
    renameTeam,
    setManagementGroupScopes,
    withdrawTeamFromQueue,
} from "./api";
import type {
    AddAdminInput,
    AddTeamMemberInput,
    CreateManagementGroupInput,
    CreateTeamInput,
    DeleteTeamInput,
    RemoveAdminInput,
    RemoveTeamMemberInput,
    RenameManagementGroupInput,
    RenameTeamInput,
    SetOwnedScopesInput,
    TeamQueueInput,
} from "./api";
import type { ManagementGroup, Team } from "./types";

const managementGroupKeys = {
    all: ["management-groups"] as const,
    list: (includeDeleted: boolean) => ["management-groups", "list", includeDeleted] as const,
    detail: (id: string) => ["management-groups", "detail", id] as const,
};

export function useManagementGroups(includeDeleted = false) {
    const client = useApiClient();
    return useQuery({
        queryKey: managementGroupKeys.list(includeDeleted),
        queryFn: () => fetchManagementGroups(client, includeDeleted),
        staleTime: 30_000,
        refetchOnWindowFocus: true,
    });
}

export function useManagementGroup(id: string | undefined | null) {
    const client = useApiClient();
    const enabled = typeof id === "string" && id.length > 0;
    const queryKey = managementGroupKeys.detail(id ?? "");
    // Selecting another group keeps the previous detail on screen, dimmed,
    // until the next one arrives.
    const placeholder = useKeepAdjacentData(queryKey, enabled);
    return useQuery({
        queryKey,
        queryFn: () => fetchManagementGroup(client, id ?? ""),
        enabled,
        staleTime: 15_000,
        ...placeholder,
    });
}

// React Query's setQueryData rejects undefined, and our writes can return
// either a full ManagementGroup or a Team. A single helper keeps every
// mutation hook below uniformly correct.
function makeGroupMutationHandlers(
    queryClient: ReturnType<typeof useQueryClient>,
    idFromResult?: (result: ManagementGroup | Team) => string,
) {
    return {
        onSuccess: (result: ManagementGroup | Team) => {
            const id = idFromResult?.(result);
            if (id !== undefined && "admins" in result) {
                queryClient.setQueryData(managementGroupKeys.detail(id), result);
            }
            void queryClient.invalidateQueries({ queryKey: managementGroupKeys.all });
        },
    };
}

export function useCreateManagementGroup() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateManagementGroupInput) => createManagementGroup(client, input),
        ...makeGroupMutationHandlers(queryClient, (g) => (g as ManagementGroup).id),
    });
}

export function useRenameManagementGroup() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: RenameManagementGroupInput) => renameManagementGroup(client, input),
        ...makeGroupMutationHandlers(queryClient, (g) => (g as ManagementGroup).id),
    });
}

export function useDeleteManagementGroup() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteManagementGroup(client, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: managementGroupKeys.all });
        },
    });
}

export function useAddManagementGroupAdmin() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: AddAdminInput) => addManagementGroupAdmin(client, input),
        ...makeGroupMutationHandlers(queryClient, (g) => (g as ManagementGroup).id),
    });
}

export function useRemoveManagementGroupAdmin() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: RemoveAdminInput) => removeManagementGroupAdmin(client, input),
        ...makeGroupMutationHandlers(queryClient, (g) => (g as ManagementGroup).id),
    });
}

export function useSetManagementGroupScopes() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: SetOwnedScopesInput) => setManagementGroupScopes(client, input),
        ...makeGroupMutationHandlers(queryClient, (g) => (g as ManagementGroup).id),
    });
}

export function useCreateTeam() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateTeamInput) => createTeam(client, input),
        onSuccess: (_team, variables) => {
            void queryClient.invalidateQueries({
                queryKey: managementGroupKeys.detail(variables.id),
            });
            void queryClient.invalidateQueries({ queryKey: managementGroupKeys.all });
        },
    });
}

export function useRenameTeam() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: RenameTeamInput) => renameTeam(client, input),
        onSuccess: (_team, variables) => {
            void queryClient.invalidateQueries({
                queryKey: managementGroupKeys.detail(variables.id),
            });
        },
    });
}

export function useDeleteTeam() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: DeleteTeamInput) => deleteTeam(client, input),
        onSuccess: (_void, variables) => {
            void queryClient.invalidateQueries({
                queryKey: managementGroupKeys.detail(variables.id),
            });
            void queryClient.invalidateQueries({ queryKey: managementGroupKeys.all });
        },
    });
}

export function useAddTeamMember() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: AddTeamMemberInput) => addTeamMember(client, input),
        onSuccess: (_team, variables) => {
            void queryClient.invalidateQueries({
                queryKey: managementGroupKeys.detail(variables.id),
            });
        },
    });
}

export function useRemoveTeamMember() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: RemoveTeamMemberInput) => removeTeamMember(client, input),
        onSuccess: (_team, variables) => {
            void queryClient.invalidateQueries({
                queryKey: managementGroupKeys.detail(variables.id),
            });
        },
    });
}

export function usePushTeamToQueue() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: TeamQueueInput) => pushTeamToQueue(client, input),
        onSuccess: () => {
            // Queue membership changed; invalidate the queues cache so the
            // queue admin page reflects the new roster on next visit.
            void queryClient.invalidateQueries({ queryKey: ["queues"] });
        },
    });
}

export function useWithdrawTeamFromQueue() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: TeamQueueInput) => withdrawTeamFromQueue(client, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["queues"] });
        },
    });
}
